import React, { useState, useContext, useEffect, useMemo } from "react";
import apiClient from "../../ApiEndpoint/apiClient";
import apiUrl from "../../ApiEndpoint/config";
import { Tabs, Select } from "antd";
import { List, Typography, Space, Empty, Button, Collapse } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import "./sitedetails.css";
import { RegisterIdContext } from "../../hooks/RegisterIdContext";
import Spinner from "../Spinner/spinner.js";
import { Subject } from "rxjs";
import { debounceTime, switchMap, catchError, filter } from "rxjs/operators";
import logo from "../../assets/veoliaLogo.png";
import { of, from } from "rxjs";




const { TabPane } = Tabs;
const { Option } = Select;
const { Panel } = Collapse;


const SiteTabs = ({ data }) => {
  const { registerId } = data;
  const [facSustainability, setFacSustainibility] = useState(null);
  const registerID = registerId?.[0]?.REGISTRY_ID
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const { groupName, facName } = useContext(RegisterIdContext);
  const { npdesOptions, setNpdesOptions, selectedNpdesId, setSelectedNpdesId, facilityKpis, setFacilityKpis, structuredViolations, setStructuredViolations, enforcementData, setEnforcementData, setRegisteridContext, tableFacilityObject, setRegisterid, setIsFacilitiesEmpty, setGroupName, setOppProjects, facilityGroupName } = useContext(RegisterIdContext);
  const [newsData, setNewsData] = useState([]);
  const [newsloading, setNewsLoading] = useState(true);


  useEffect(() => {
    if (registerID) {
      const fetchFacilitySustanibilityData = async () => {
        try {
          const response = await apiClient.get(`${apiUrl}/facility_sustainability?registry_id=${registerID}`);
          setFacSustainibility(response?.data);
        } catch (error) {
          console.error("Error fetching facility data:", error);
          setFacSustainibility({ Message: "An error occurred. Please contact the Administrator." });
        }
      };

      fetchFacilitySustanibilityData();
    }
  }, [registerID]); // Runs when REGISTRY_ID changes


  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);

        // Always pass as group_name, using facName if groupName is not available
        const contactinformation = groupName || facilityGroupName;

        const response = await apiClient.get(`${apiUrl}/get_contacts`, {
          params: { group_name: contactinformation },
        });

        setContacts(response.data.contacts || []);
      } catch (error) {
        console.error("Error fetching contacts:", error);
      } finally {
        setLoading(false);
      }
    };

    if (groupName || facilityGroupName) {
      fetchContacts();
    }
  }, [groupName, facilityGroupName]);


  //npdes api integration

  useEffect(() => {
    if (registerID) {
      const fetchNpdesPermits = async () => {
        try {
          const response = await apiClient.get(`${apiUrl}/npdes_permits?registry_id=${registerID}`);
          const permits = response?.data?.npdes_permits || [];
          const options = permits.map(permit => ({
            value: permit.npdes_id,
            label: permit.display_label,
          }));
          setNpdesOptions(options);

          // ✅ Set default value if any option is available
          if (options.length > 0) {
            setSelectedNpdesId(options[0].value);
          }

        } catch (error) {
          console.error("Error fetching NPDES permits:", error);
        }
      };

      fetchNpdesPermits();
    }
  }, [registerID]);


  //npdes selected value api integration and also if npdes not selected
  useEffect(() => {
    const fetchNpdesOverview = async () => {
      // If no ID, still set default violations
      if (!selectedNpdesId) {
        const defaultStructured = requiredViolationTypes.map((type) => ({
          key: type.key,
          label: type.label,
          date: null,
          description: null,
          bothMissing: true,
        }));
        setStructuredViolations(defaultStructured);
        setFacilityKpis({});
        setEnforcementData(null);
        return;
      }

      try {
        const response = await apiClient.get(`${apiUrl}/npdes_all_info`, {
          params: { npdes_id: selectedNpdesId },
        });

        setFacilityKpis(response?.data?.facility_kpis?.[0] || {});

        const allViolations = response?.data?.open_violations || [];

        const structured = requiredViolationTypes.map((type) => {
          const match = allViolations.find((v) => v.viol_class_key === type.key);
          const bothMissing = !match?.viol_date && !match?.violation_desc;

          return {
            key: type.key,
            label: type.label,
            date: bothMissing ? null : match?.viol_date ?? "N/A",
            description: bothMissing ? null : match?.violation_desc ?? "N/A",
            bothMissing,
          };
        });

        setStructuredViolations(structured);
        setEnforcementData(response?.data?.enforcement_actions?.[0] || null);

      } catch (error) {
        console.error("Error fetching NPDES overview:", error);
      }
    };

    // Required violation types outside function so it's accessible in both branches npdes present and not present
    const requiredViolationTypes = [
      { key: "EFF", label: "Effluent violations" },
      { key: "SE", label: "Single Event violations" },
      { key: "CS", label: "Compliance Schedule violations" },
      { key: "PS", label: "Permit Schedule violations" },
    ];

    fetchNpdesOverview();
  }, [selectedNpdesId]);



  //news api integration

  useEffect(() => {
    if (!registerID) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const news$ = of(registerID).pipe(
      debounceTime(500), // wait 600ms after last change
      filter((id) => !!id), // only run if ID is truthy
      switchMap((id) =>
        from(
          apiClient.get(`${apiUrl}/facility_news`, {
            params: { registry_id: id },
            signal,
          })
        ).pipe(
          catchError((error) => {
            console.error("Error fetching news:", error);
            setNewsData([]); // clear data on error
            return of(null);
          })
        )
      )
    );

    setNewsLoading(true);

    const subscription = news$.subscribe((response) => {
      if (response?.data) {
        setNewsData(response.data);
      }
      setNewsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      controller.abort(); // cancel API call on cleanup
    };
  }, [registerID]);



  //veolia project api integration
  useEffect(() => {
    apiClient.get(`${apiUrl}/get_matched_accounts_opportunities?registry_id=${registerID}`)
      .then(response => {
        const oppProjects = response.data || null;
        setOppProjects(oppProjects);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching matched accounts/opportunities:", err);
        setOppProjects(null);
        setLoading(false);
      });
  }, [registerID]);






  const [selectedRegistryId, setSelectedRegistryId] = useState(null);

  // Reset selected facility when tableFacilityObject changes
  // Set first registryId by default when data comes in
  useEffect(() => {
    if (tableFacilityObject?.length > 0) {
      const first = tableFacilityObject[0];
      setSelectedRegistryId(first.registryId);
      fetchFacilityInfo(first.registryId);
    }
  }, [tableFacilityObject]);


  // function to fetch facility info
  const fetchFacilityInfo = async (registryId) => {
    try {
      const facilityResponse = await apiClient.get(
        `${apiUrl}/facility_info?registry_id=${registryId}`
      );
      const facilityData = facilityResponse.data?.data || [];
      setRegisterid(facilityData);
    } catch (error) {
      console.error("Error fetching facility info:", error);
    }
  };



  return (
    <>
      <div className="card" style={{ padding: '10px' }}>
        {tableFacilityObject.length > 1 && (
          <div className="row">
            <div className="col-8" style={{ fontSize: '14px', marginTop: "14px" }}>
              Multiple EPA Facilities found for the selected IIR Plant / Site
            </div>
            <div className="col-4">
              <Select
                className="filter-select"
                style={{ marginTop: '10px' }}
                showSearch
                allowClear
                value={selectedRegistryId}

                filterOption={(input, option) =>
                  option?.children.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => {
                  if (!value) {
                    // 👇 reset back to first value
                    if (tableFacilityObject?.length > 0) {
                      const first = tableFacilityObject[0];
                      setSelectedRegistryId(first.registryId);
                      fetchFacilityInfo(first.registryId);
                    }
                    return;
                  }

                  setSelectedRegistryId(value);
                  fetchFacilityInfo(value);
                }}
              >

                {tableFacilityObject.map((facility) => (
                  <Option key={facility.registryId} value={facility.registryId}>
                    {facility.facName}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        )}

        <div className="mt-2">
          <Tabs defaultActiveKey="1">
            <TabPane tab="Site Details" key="1">
              <CompanyDetails companydata={registerId} loading={loading} />
            </TabPane>
            <TabPane tab="Sustainability Goals " key="2">
              <SustainabilityGoals companydata={registerId} facSustainabilityData={facSustainability} />
            </TabPane>
            <TabPane tab="Contacts" key="3">
              <Contacts contacts={contacts} loading={loading} />
            </TabPane>
            <TabPane tab="News" key="4">
              <News newsData={newsData} newsloading={newsloading} />
            </TabPane>
          </Tabs>
        </div>
      </div>
    </>

  );
};



const CompanyDetails = ({ companydata, loading }) => {
  const { oppProjects } = useContext(RegisterIdContext);
  return (
    <Tabs defaultActiveKey="1">
      <TabPane tab="Overview" key="1">
        <Overview companyoverview={companydata[0]} />
      </TabPane>
      <TabPane tab="Permits" key="4">
        <Permits />
      </TabPane>
      <TabPane tab="Violation" key="2">
        <Violation companyoverview={companydata[0]} />
      </TabPane>
      <TabPane tab="Enforcement" key="3">
        <Enforcement />
      </TabPane>
      {oppProjects && oppProjects.length > 0 && (
        <TabPane tab="Veolia Footprint" key="5">
          <VeoliaSites loading={loading} />
        </TabPane>
      )}
    </Tabs>
  );
};

const Overview = ({ companyoverview }) => {
  const { registerId, plantDetails, setPlantDetails, plantOptions, setPlantOptions, selectedPlantId, setSelectedPlantId, selectedPlant, setSelectedPlant, setIsFacilitiesEmpty, refineSearchPlantID, setRefineSearchPlantID } = useContext(RegisterIdContext);
  const [facilityData, setFacilityData] = useState("");
  const [facilitySummary, setFacilitySummary] = useState("");
  const [loading, setLoading] = useState(true);
  const details = companyoverview;
  const { Message, Citations } = facilitySummary;
  let registerID = details?.REGISTRY_ID
  const registryIdSubject = new Subject();
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);

  //create  adropdown if is empty is false and if it has more than 1 value

  useEffect(() => {
    const subscription = registryIdSubject
      .pipe(
        debounceTime(500), // delay execution
        switchMap((registryId) =>
          Promise.all([
            apiClient.get(`${apiUrl}/facility_info?registry_id=${registryId}`),
            apiClient.get(`${apiUrl}/facility_summary?registry_id=${registryId}`),
            // apiClient.get(`${apiUrl}/get_matched_accounts_opportunities?registry_id=${registryId}`)
          ])
        )
      )
      .subscribe({
        next: ([response1, response2, response3]) => {
          const facInfo = response1.data?.data?.[0] || null;
          const summary = response2.data;
          //const oppProjects = response3?.data || null;
          setFacilityData(facInfo);
          setFacilitySummary(summary);
          // setOppProjects(oppProjects);
          setLoading(false);
          setIsSummaryLoading(false);
        },
        error: (err) => {
          console.error("Error fetching facility data:", err);
          setFacilitySummary({ Message: "An error occurred. Please contact the Administrator." });
          setLoading(false);
          setIsSummaryLoading(false);
        }
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [registerID]);

  useEffect(() => {
    if (companyoverview?.REGISTRY_ID) {
      setLoading(true);
      registryIdSubject.next(companyoverview.REGISTRY_ID); // trigger observable pipeline
    }
  }, [companyoverview?.REGISTRY_ID]);



  //plant api integration
  useEffect(() => {
    if (!registerID && !refineSearchPlantID) return;
    const fetchPlantDetails = async () => {
      try {
        // ✅ Build params dynamically
        const params = {};
        if (refineSearchPlantID) {
          params.plantId = refineSearchPlantID;
        } else if (registerID) {
          params.registryId = registerID;
        }

        // const { data } = await apiClient.get(`${apiUrl}/plants-detail?registryId=${registerID}`);
        const { data } = await apiClient.get(`${apiUrl}/plants-detail`, { params });
        const plants = data?.plants || [];
        setPlantDetails(plants);

        if (plants.length > 0) {
          const options = plants.map(p => ({
            value: String(p.plantId),
            label: p.plantName,
          }));

          setPlantOptions(options);
          const defaultPlantId = String(plants[0].plantId);
          setSelectedPlantId(defaultPlantId);
          setSelectedPlant(plants[0]);
        } else {
          setPlantOptions([]);
          setSelectedPlantId("");
          setSelectedPlant({});
        }
      } catch (error) {
        console.error("Error fetching plant details:", error);
        setPlantDetails([]);
        setPlantOptions([]);
        setSelectedPlantId("");
        setSelectedPlant({});
      }
    };

    fetchPlantDetails();
  }, [registerID, refineSearchPlantID]);

  useEffect(() => {
    const foundPlant = plantDetails.find(p => String(p.plantId) === selectedPlantId);
    if (foundPlant) {
      setSelectedPlant(foundPlant);
    }
  }, [selectedPlantId, plantDetails]);


  //format plant date 
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const cleaned = dateString.replace(/\[.*\]/, ""); // remove [UTC] or similar
    const date = new Date(cleaned);
    return date.toLocaleDateString("en-US");
  };

  //format plant address
  const getFormattedMailingAddress = (address) =>
    [
      address?.addressLine1,
      address?.city,
      address?.stateName,
      address?.postalCode,
      address?.countyName,
    ]
      .filter(Boolean)
      .join(", ") || "N/A";

  //format plant phonenumber
  const formatPhoneNumber = (phone) =>
    !phone || !phone.number || phone.cc === 0 ? "N/A" : `+${phone.cc} ${phone.number}`;

  //MailingAddress mapped here from the selectedPlant
  const mailingAddress = selectedPlant?.mailingAddress || {};

  if (loading) {
    return <Spinner />; // ✅ Show spinner while loading
  }


  return (
    <>
      <div style={{ maxHeight: "60vh", overflowY: "scroll" }}>

        {facilityData.IS_VEOLIA_SITE && (
          <>
            <img
              src={logo}
              alt="Veolia Logo"
              className="img-fluid"
              style={{ height: "45px", marginBottom: "6px" }}
            />
            <span className="leftlabel">Customer Site</span>
          </>
        )}

        <div className="container p-0">
          <h5 className="site-title">
            {details?.FAC_NAME || facilityData?.FAC_NAME || "N/A"}
          </h5>

          <div>
            {isSummaryLoading ? (
              <p className="blinking-text">Loading description...</p>
            ) : (
              <>
                <p className="site-description">{Message}</p>
                {Citations && Citations.length > 0 && (
                  <ul>
                    {Citations.map((citation, index) => {
                      const match = citation.match(/\[([^\]]+)\]\(([^)]+)\)/);
                      if (!match) return null;

                      const label = match[1];
                      const url = match[2];

                      return (
                        <li key={index}>
                          <span>{label}:</span>{" "}
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            {url}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>



          <div className="details-report mb-3">
            <span className="leftlabel">Detail EPA Facility Report: </span>
            <a
              href={`http://echo.epa.gov/detailed-facility-report?fid=${registerID}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              http://echo.epa.gov/detailed-facility-report?fid={registerID}
            </a>
          </div>

          <h6 style={{ fontSize: "18px" }}>Facility Information</h6>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <p className="details-report">Electricity Consumption Price:</p>
            <span>{registerId[0]?.electricity_consumption_price + " ¢/kWh"}</span>
          </div>



        </div>

        {/* plant reponse mapping start here*/}

        {plantDetails.length > 0 && (
          <div className="opportunity">
            <h6 style={{ fontSize: "18px", paddingBottom: "6px" }}>Plant Details</h6>
            {plantOptions.length > 1 && (
              <div>
                <h6>Multiple IIR Plant / Site found for the selected EPA Facility</h6>
                <Select
                  className="filter-select"
                  showSearch
                  placeholder="Select Plant"
                  optionFilterProp="children"
                  allowClear
                  onChange={value => {
                    if (!value) {
                      setSelectedPlantId("");
                      setSelectedPlant({});
                    } else {
                      setSelectedPlantId(value);
                    }
                  }}
                  value={selectedPlantId || ""}
                  style={{ width: 400, marginBottom: 20 }}
                >
                  {plantOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </div>
            )}


            <div className="detection-item">
              <p>
                <span className="left">Plant Name:</span>
                <span className="right">{selectedPlant?.plantName ?? "N/A"}</span>
              </p>

              <p>
                <span className="left">Number of employees:</span>
                <span className="right">{selectedPlant?.noEmployees ?? "N/A"}</span>
              </p>
              <p>
                <span className="left">Startup date:</span>
                <span className="right">{formatDate(selectedPlant?.startupDate)}</span>
              </p>
              <p>
                <span className="left">Close date:</span>
                <span className="right">{formatDate(selectedPlant?.closeDate)}</span>
              </p>
              <p>
                <span className="left">Existing Sq.Ft.:</span>
                <span className="right">
                  {selectedPlant?.existingSqFt
                    ? `${Number(selectedPlant.existingSqFt).toLocaleString("en-US")} sq ft`
                    : "N/A"}
                </span>
              </p>
            </div>

            <div className="detection-item">
              <h6 style={{ fontSize: "18px" }}>Mailing Address</h6>
              <p>
                <span className="left" style={{ width: '10rem' }}>Address Line 1:</span>
                <span className="right">{getFormattedMailingAddress(mailingAddress)}</span>
              </p>
              <p>
                <span className="left">Plant Phone:</span>
                <span className="right">{formatPhoneNumber(selectedPlant?.phone)}</span>
              </p>

            </div>

            <div className="detection-item">
              <h6 style={{ fontSize: "18px" }}>Plant Industry Sector</h6>
              {selectedPlant?.sectors?.length > 0 ? (
                selectedPlant.sectors.map((sector, index) => (
                  <p key={index}>
                    <span className="left">{sector.plantSectorDesc ?? "N/A"}</span>
                  </p>
                ))
              ) : (
                <p>
                  <span className="left">N/A</span>
                </p>
              )}
            </div>

            <div className="detection-item">
              <h6 style={{ fontSize: "18px" }}>Fuel</h6>
              <p>
                <span className="left">Primary Fuel:</span>
                <span className="right">{selectedPlant?.fuel?.primaryFuel || 'N/A'}</span>
              </p>
              <p>
                <span className="left">Secondary Fuel:</span>
                <span className="right">{selectedPlant?.fuel?.secondaryFuel || 'N/A'}</span>
              </p>

            </div>

            <div className="detection-item">

              <p>
                <span className="left" style={{ fontSize: "18px", color: 'black' }}>Cogen/CHP:</span>
                <span className="right">{selectedPlant?.cogenChp || '0'}</span>
              </p>


            </div>

            <div className="detection-item">
              <h6 style={{ fontSize: "18px" }}>SIC Product</h6>
              {selectedPlant?.sicProduct?.length > 0 ? (
                selectedPlant.sicProduct.map((sector, index) => (
                  <p key={index}>
                    <span className="left">{sector.sicProductDesc ?? "N/A"}</span>
                  </p>
                ))
              ) : (
                <p>
                  <span className="left">N/A</span>
                </p>
              )}
            </div>


            <div className="opportunity-container">

              {selectedPlant?.owner?.map((owner, index) => (
                <React.Fragment key={index}>
                  <h2 className="opp-main-heading">OWNERS</h2>
                  <div className="opportunity" key={index}>
                    <div className="opportunity-name">
                      {owner.companyName ?? "N/A"}
                    </div>
                    <ul className="opportunity-details">
                      <li>
                        <strong>Classification:</strong>{" "}
                        {owner.classificationDesc ?? "N/A"}
                      </li>
                      <li>
                        <strong>Ownership percentage:</strong>{" "}
                        {owner.ownerPercent != null ? `${owner.ownerPercent}%` : "N/A"}
                      </li>
                      <li>
                        <strong>Website:</strong>{" "}
                        {owner.companyWebsite ? (
                          <a href={owner.companyWebsite} target="_blank" rel="noopener noreferrer">
                            {owner.companyWebsite}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </li>
                    </ul>
                  </div>
                </React.Fragment>
              ))}


              {selectedPlant?.parent && (
                <>
                  <h2 className="opp-main-heading">PARENT</h2>
                  <div className="opportunity">
                    <div className="opportunity-name">
                      {selectedPlant.parent.companyName ?? "N/A"}
                    </div>
                    <ul className="opportunity-details">
                      <li>
                        <strong>Website:</strong>{" "}
                        {selectedPlant.parent.website ? (
                          <a
                            href={
                              selectedPlant.parent.website.startsWith("http")
                                ? selectedPlant.parent.website
                                : `https://${selectedPlant.parent.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {selectedPlant.parent.website}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </li>
                    </ul>
                  </div>
                </>
              )}


              {selectedPlant?.capacity?.map((capacity, index) => (
                <React.Fragment key={index}>
                  <h2 className="opp-main-heading">Capacity</h2>
                  <div className="opportunity" key={index}>
                    <div className="opportunity-name">
                      {capacity.productDesc ?? "N/A"}
                    </div>
                    <ul className="opportunity-details">
                      <li>
                        <strong>Type:</strong>{" "}
                        {capacity.type ?? "N/A"}
                      </li>
                      <li>
                        <strong>Actual Capacity:</strong>{" "}
                        {capacity.actualCapacity ?? "N/A"}
                      </li>
                      <li>
                        <strong>Design Capacity:</strong>{" "}
                        {capacity.designCapacity ?? "N/A"}
                      </li>
                      <li>
                        <strong>Unit of Measures:</strong>{" "}
                        {capacity.uom ?? "N/A"}
                      </li>
                    </ul>
                  </div>
                </React.Fragment>
              ))}

            </div>

          </div>
        )}
      </div>
    </>
  );
};

//Permits data 
const Permits = () => {
  const { facilityKpis, setSelectedNpdesId, selectedNpdesId, npdesOptions } = useContext(RegisterIdContext);
  return (
    <>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>


        <Select
          className="filter-select"
          showSearch
          placeholder="Select NPDES ID"
          optionFilterProp="children"
          onChange={(value) => {
            setSelectedNpdesId(value);
          }}
          value={selectedNpdesId}
          allowClear

        >
          {npdesOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>

      </div>

      <h6 className="details-title mt-3">
        NPDES (NATIONAL POLLUTANT DISCHARGE ELIMINATION SYSTEM)
      </h6>

      <div className="detection-item">
        <p>
          <span className="left">Facility WW discharge capacity (MGD): </span>
          <span className="right">
            {facilityKpis?.["Facility WW discharge capacity MGD"] ?? "N/A"}
          </span>
        </p>
        <p>
          <span className="left">Average WW discharge (MGD):</span>
          <span className="right">
            {facilityKpis?.["Average WW discharge MGD"] ?? "N/A"}
          </span>
        </p>
        <p>
          <span className="left">Major Flag:</span>
          <span className="right">N/A</span>
        </p>{" "}
        <p>
          <span className="left">Permit Type:</span>
          <span className="right">
            {facilityKpis?.["Permit Type"] ?? "N/A"}
          </span>
        </p>
        <p>
          <span className="left">Days to expiry:</span>
          <span className="right">
            {facilityKpis?.["Days to expiry"] ?? "N/A"}
          </span>
        </p>
        <p>
          <span className="left">Permit Status:</span>
          <span className="right">
            {facilityKpis?.["Permit Status"] ?? "N/A"}
          </span>
        </p>
        <p>
          <span className="left">DMR Effluent Exceedances (Last 12 mo):</span>
          <span className="right">
            {facilityKpis?.["DMR Effluent Exceedances Last 12 mo"] ?? "0"}
          </span>
        </p>
        <p>
          <span className="left">Distinct pollutants involved:</span>
          <span className="right">
            {facilityKpis?.["Distinct pollutants involved"] ?? "0"}
          </span>
        </p>
        {/* <p>
          <span className="left">
            Open permit/compliance-schedule violations:
          </span>
          <span className="right">
            {facilityKpis?.["Open permit compliance‑schedule violations"] ??
              "0"}
          </span>
        </p>
        <p>
          <span className="left">
            Single-event violations in the last 3 years:
          </span>
          <span className="right">
            {facilityKpis?.["Single‑event violations in the last 3 years"] ??
              "0"}
          </span>
        </p>
        <p>
          <span className="left">Total penalties in the last 5 years:</span>
          <span className="right">
            {facilityKpis?.["Total penalties in the last 5 years"] ?? "0"}
          </span>
        </p> */}
      </div>
    </>
  )
}



const Violation = ({ companyoverview }) => {
  const { npdesOptions, setNpdesOptions, selectedNpdesId, setSelectedNpdesId, facilityKpis, setFacilityKpis, structuredViolations, setStructuredViolations } = useContext(RegisterIdContext);
  const { violation } = useContext(RegisterIdContext);
  const [violationData, SetViolationData] = useState("")
  const [loading, setLoading] = useState(true);
  const [violationSummary, SetViolationSummary] = useState(null)

  //To get the PERMIT_TYPE_CODE
  const uniquePermitTypeCodes = [...new Set(violation?.permit_details?.map(item => item.PERMIT_TYPE_CODE) || [])];

  let registerId = companyoverview?.REGISTRY_ID

  useEffect(() => {
    if (registerId) {

      const fetchFacilityData = async () => {
        setLoading(true);
        try {
          const response = await apiClient.get(`${apiUrl}/npdes_facility_violations?registry_id=${registerId}`);
          const registerIdData = response.data
          SetViolationData(registerIdData);
        } catch (error) {
          console.error("Error fetching facility data:", error);
        } finally {
          setLoading(false); // ✅ Stop loading
        }
      };

      fetchFacilityData();
    }
  }, [companyoverview?.REGISTRY_ID]); // Runs when REGISTRY_ID changes

  useEffect(() => {
    if (registerId) {
      const fetchFacilityViolations = async () => {
        setLoading(true);
        try {
          const violation_summary = await apiClient.get(`${apiUrl}/violation_summary?registry_id=${registerId}`);
          SetViolationSummary(violation_summary?.data)
        } catch (error) {
          console.error("An unexpected error occurred:", error.message);
          SetViolationSummary({ Summary: "An error occurred. Please contact the administrator." });
        } finally {
          setLoading(false); // ✅ Stop loading
        }
      };

      fetchFacilityViolations();
    }
  }, [companyoverview?.REGISTRY_ID]); // Runs when REGISTRY_ID changes




  return (
    <div style={{ maxHeight: "60vh", overflowY: "scroll" }}>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>


        <Select
          className="filter-select"
          showSearch
          placeholder="Select NPDES ID"
          optionFilterProp="children"
          onChange={(value) => {
            setSelectedNpdesId(value);
          }}
          value={selectedNpdesId}
          allowClear

        >
          {npdesOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>

      </div>



      <div className="container p-0">
        <h5 className="site-title">EPA VIOLATION SUMMARY</h5>
        <p className="site-description">
          {violationSummary?.Summary === undefined ||
            violationSummary?.Summary === null
            ? "Loading..."
            : violationSummary.Summary.trim()
              ? violationSummary.Summary
              : `Sorry, we don't have the violation summary at the moment. We're working on getting this updated as soon as possible. Thanks for your patience, and feel free to check back later!`}
        </p>
      </div>



      <div className="site-details">

        <h6 className="details-title">OPEN VIOLATIONS</h6>

        {structuredViolations.map((item, index) => (
          <div className="detection-item" key={index}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="left" style={{ fontWeight: 600 }}>{item.label}</span>
              {item.bothMissing && <span className="right">None</span>}
            </div>

            {!item.bothMissing && (
              <>
                <p>
                  <span className="left">Violation date:</span>
                  <span className="right">{item.date || "null"}</span>
                </p>
                <p>
                  <span className="left">Violation description:</span>
                  <span className="right">{item.description || "null"}</span>
                </p>
              </>
            )}
          </div>
        ))}
      </div>

    </div>
  );
};


const Enforcement = () => {
  const { npdesOptions, setNpdesOptions, selectedNpdesId, setSelectedNpdesId, facilityKpis, setFacilityKpis, structuredViolations, setStructuredViolations, enforcementData, setEnforcementData } = useContext(RegisterIdContext);
  const renderValue = (val) =>
    val !== null && val !== undefined ? val : <span className="right">None</span>;

  const isFormalEmpty =
    !enforcementData?.formal_date_recent &&
    !enforcementData?.formal_type_recent &&
    !enforcementData?.formal_penalty_recent &&
    !enforcementData?.formal_date_2 &&
    !enforcementData?.formal_type_2 &&
    !enforcementData?.formal_penalty_2 &&
    !enforcementData?.formal_date_3 &&
    !enforcementData?.formal_type_3 &&
    !enforcementData?.formal_penalty_3;

  const isInformalEmpty =
    !enforcementData?.informal_date_recent &&
    !enforcementData?.informal_type_recent &&
    !enforcementData?.informal_date_2 &&
    !enforcementData?.informal_type_2 &&
    !enforcementData?.informal_date_3 &&
    !enforcementData?.informal_type_3;

  return (
    <>
      <div style={{ maxHeight: "60vh", minHeight: "60vh", overflowY: "scroll" }}>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>


          <Select
            className="filter-select"
            showSearch
            placeholder="Select NPDES ID"
            optionFilterProp="children"
            onChange={(value) => {
              setSelectedNpdesId(value);
            }}
            value={selectedNpdesId}
            allowClear

          >
            {npdesOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>

        </div>

        {/* Enforcement Details */}

        <div className="detection-item">
          <h6 className="details-title mb-4">Enforcement Actions</h6>


          <strong className="enforcement-title">Formal actions (Last 3)</strong>
          {isFormalEmpty ? (
            <span style={{ float: 'right' }}>None</span>
          ) : (
            <>
              <p><span className="left">Action date (recent):</span><span className="right">{renderValue(enforcementData.formal_date_recent)}</span></p>
              <p><span className="left">Action type (recent):</span><span className="right">{renderValue(enforcementData.formal_type_recent)}</span></p>
              <p><span className="left">Penalty amount (recent):</span><span className="right">{renderValue(enforcementData.formal_penalty_recent)}</span></p>

              <p><span className="left">Action date:</span><span className="right">{renderValue(enforcementData.formal_date_2)}</span></p>
              <p><span className="left">Action type:</span><span className="right">{renderValue(enforcementData.formal_type_2)}</span></p>
              <p><span className="left">Penalty amount:</span><span className="right">{renderValue(enforcementData.formal_penalty_2)}</span></p>

              <p><span className="left">Action date (earliest):</span><span className="right">{renderValue(enforcementData.formal_date_3)}</span></p>
              <p><span className="left">Action type (earliest):</span><span className="right">{renderValue(enforcementData.formal_type_3)}</span></p>
              <p><span className="left">Penalty amount (earliest):</span><span className="right">{renderValue(enforcementData.formal_penalty_3)}</span></p>
            </>
          )}
        </div>

        {/* Last 10 years penalty */}
        <div className="detection-item mt-2">
          <strong className="enforcement-title">Last 10 years penalty amount:</strong>
          <span style={{ float: 'right' }}>{renderValue(enforcementData?.total_penalty_10yr)}</span>
        </div>

        {/* Informal Actions */}
        <div className="detection-item mt-2">
          <strong className="enforcement-title">Informal actions (Last 3)</strong>
          {isInformalEmpty ? (
            <span style={{ float: 'right' }}>None</span>
          ) : (
            <>
              <p><span className="left">Action date (recent):</span><span className="right">{renderValue(enforcementData.informal_date_recent)}</span></p>
              <p><span className="left">Action type (recent):</span><span className="right">{renderValue(enforcementData.informal_type_recent)}</span></p>

              <p><span className="left">Action date:</span><span className="right">{renderValue(enforcementData.informal_date_2)}</span></p>
              <p><span className="left">Action type:</span><span className="right">{renderValue(enforcementData.informal_type_2)}</span></p>

              <p><span className="left">Action date (earliest):</span><span className="right">{renderValue(enforcementData.informal_date_3)}</span></p>
              <p><span className="left">Action type (earliest):</span><span className="right">{renderValue(enforcementData.informal_type_3)}</span></p>
            </>
          )}
        </div>
      </div>
    </>
  )
}


//veolisites api integration

const VeoliaSites = ({ loading }) => {
  const { oppProjects } = useContext(RegisterIdContext);
  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <div style={{ maxHeight: "55vh", overflowY: "scroll" }}>
          {oppProjects && oppProjects.length > 0 && (
            <div>
              <h2 className="opp-main-heading">VEOLIA'S PROJECTS (PAST/CURRENT)</h2>
              {oppProjects.map((account, index) => (
                <div key={index} className="opportunity-container">
                  <div className="account-name">Salesforce Accountname: {toTitleCase(account.account_name)}</div>
                  {account.opportunities.map((opp) => (
                    <div key={opp.opportunity_id} className="opportunity">
                      <div className="opportunity-name">
                        Project Name: {opp.opportunity_name}
                      </div>
                      <ul className="opportunity-details">
                        <li>
                          <strong>Business Unit:</strong> {opp.business_unit}
                        </li>
                        <li>
                          <strong>Business Type:</strong> {opp.business_type}
                        </li>
                        <li>
                          <strong>Start Date:</strong>{" "}
                          {new Date(opp.project_start_date).toLocaleDateString()}
                        </li>
                        <li>
                          <strong>Contract Duration:</strong>{" "}
                          {opp.contract_duration_years} year(s)
                        </li>
                        <li>
                          <strong>Veolia SPOC:</strong>{" "}
                          {opp.opportunity_team_members || "N/A"}
                        </li>
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )

}

const SustainabilityGoals = ({ companydata, facSustainabilityData }) => {
  const facilityName = companydata[0].FAC_NAME;
  if (!facSustainabilityData) {
    return <p>Loading sustainability data...</p>;
  }

  const { Message, Citations } = facSustainabilityData;
  return (
    <div>
      <p>{Message}</p>
      {Citations && Citations.length > 0 && (
        <ul>
          {Citations.map((citation, index) => {
            // Match Markdown-style [label](url)
            const match = citation.match(/\[([^\]]+)\]\(([^)]+)\)/);
            if (!match) return null;

            const label = match[1];
            const url = match[2];

            return (
              <li key={index}>
                <span>{label}:</span>{" "}
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};


const Contacts = ({ contacts, loading }) => {
  const { selectedPlant } = useContext(RegisterIdContext);
  return (

    <div style={{ maxHeight: "70vh", overflowY: "scroll" }} >

      {/* IIR Data Contact */}
      {(selectedPlant?.contact?.length > 0 || selectedPlant?.ltsa?.length > 0) && (

        <Collapse
          defaultActiveKey={['iir']}
          className="card" // 🟡 Keeps your existing card styling
          style={{ marginBottom: '16px' }}
        >
          <Panel
            header={<h2 className="opp-main-heading" style={{ margin: 0 }}>IIR Plant Contact</h2>}
            key="iir"
          >
            {/* IIR Contact */}
            {selectedPlant?.contact?.length > 0 && (
              <div className="opportunity-container">
                <h2 className="opp-main-heading">Contact</h2>
                {selectedPlant.contact.map((contact, index) => (
                  <div className="opportunity" key={index}>
                    <div className="opportunity-name">
                      {contact.firstName} {contact.lastName}
                    </div>
                    <ul className="opportunity-details">
                      <li><strong>Title:</strong> {contact.actualTitle ?? "N/A"}</li>
                      <li><strong>Functional Title:</strong> {contact.functionalTitle ?? "N/A"}</li>
                      <li><strong>On-site:</strong> {contact.onSite ?? "N/A"}</li>
                      <li>
                        <strong>Email:</strong>{" "}
                        {contact.emailAddress ? (
                          <span style={{ color: "#277bff" }}>{contact.emailAddress}</span>
                        ) : (
                          "N/A"
                        )}
                      </li>
                      <li>
                        <strong>LinkedIn:</strong>{" "}
                        {contact.linkedInId ? (
                          <a
                            href={`https://www.linkedin.com/in/${contact.linkedInId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {contact.linkedInId}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </li>

                      <li><strong>Phone Number:</strong> {contact.phone?.number ?? "N/A"}</li>
                      <li><strong>Ext:</strong> {contact.phone?.ext ?? "N/A"}</li>
                      <li><strong>Country Code:</strong> {contact.phone?.cc ? `+${contact.phone.cc}` : "N/A"}</li>
                      <li><strong>Alt Phone:</strong> {contact.altPhone?.number ?? "N/A"}</li>
                      <li><strong>Alt Ext:</strong> {contact.altPhone?.ext ?? "N/A"}</li>
                      <li><strong>Alt Country Code:</strong> {contact.altPhone?.cc ? `+${contact.altPhone.cc}` : "N/A"}</li>
                      <li>
                        <strong>Alt Phone Number:</strong>
                        {contact?.altPhoneMobile && contact.altPhoneMobile !== "0"
                          ? contact.altPhoneMobile
                          : " N/A"}
                      </li>
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* LTSA Contact */}
            {selectedPlant?.ltsa?.length > 0 && (
              <div className="opportunity-container">
                <h2 className="opp-main-heading">Long Term Service Agreement Contact</h2>
                {selectedPlant.ltsa.map((item, index) => (
                  <div className="opportunity" key={index}>
                    <div className="opportunity-name">{item.contractorName ?? "Contractor Name"}</div>
                    <ul className="opportunity-details">
                      <li><strong>LTSA ID:</strong> {item.ltsaId ?? "N/A"}</li>
                      <li><strong>Status Description:</strong> {item.statusDesc ?? "N/A"}</li>
                      <li><strong>Contract Length:</strong> {item.contractLength ?? "N/A"} {item.contractLength ? "years" : ""}</li>
                      <li>
                        <strong>Contract Expiration Date:</strong>{" "}
                        {item.contractExpiration
                          ? new Date(item.contractExpiration.replace(/\[.*\]/, "")).toLocaleDateString("en-US")
                          : "N/A"}
                      </li>
                      <li><strong>Average Number of Personnel:</strong> {item.personnelCountAvg ?? "N/A"}</li>
                      <li><strong>Peak Number of Personnel:</strong> {item.personnelCountPeak ?? "N/A"}</li>
                      <li><strong>LTSA Preferred Contractor:</strong> {item.preferredContractor === "1" ? "Yes" : "No"}</li>
                      <li>
                        <strong>QC Date:</strong>{" "}
                        {item.qcDate
                          ? new Date(item.qcDate.replace(/\[.*\]/, "")).toLocaleDateString("en-US")
                          : "N/A"}
                      </li>
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </Collapse>
      )}

      {/* Salesforce Data Contact */}

      {!loading && contacts.length > 0 && (
        <Collapse
          defaultActiveKey={['sf']}
          className="card mt-4"
          style={{ marginBottom: '16px' }}
        >
          <Panel
            header={<h2 className="opp-main-heading" style={{ margin: 0 }}>Salesforce Contacts</h2>}
            key="sf"
          >
            <div className="opportunity-container">
              <h2 className="opp-main-heading">Contact</h2>
              {contacts.map((contact, index) => (
                <div className="opportunity" key={index}>
                  <div className="opportunity-name">
                    {contact.CONTACT_NAME || "Contact"}{" "}
                    <span style={{ color: "#FFAC00", fontWeight: 500 }}>
                      ({contact.EMPLOYEE_STATUS__C || "Status Unknown"})
                    </span>
                  </div>
                  <ul className="opportunity-details">
                    <li><strong>Name:</strong> {contact.ACCOUNT_NAME || "N/A"}</li>
                    <li><strong>Job Description:</strong> {contact.JOBCODEDESCRIPTION__C || contact.TITLE || "N/A"}</li>
                    <li><strong>Phone:</strong> {contact.MOBILE_PHONE || "N/A"} {contact.ASSISTANT_PHONE ? `| Assistant: ${contact.ASSISTANT_PHONE}` : ""}</li>
                    <li><strong>Do Not Call:</strong> {contact.DO_NOT_CALL ? "Yes" : "No"}</li>
                    <li><strong>Email:</strong> <span className="text-primary">{contact.EMAIL}</span> ({contact.EMAIL_VERIFICATION_STAUTS__C || "Unknown"})</li>
                    <li><strong>Opted Out of Email:</strong> {contact.HAS_OPTED_OUT_OF_EMAIL ? "Yes" : "No"}</li>
                    <li><strong>HubSpot Score:</strong> {contact.HS_HUBSPOT_SCORE__C || "N/A"}</li>
                    <li><strong>Engagement:</strong> {contact.HS_TEMPERATURE__C || "N/A"}</li>
                    <li><strong>Related Facility:</strong> {contact.PLANT_NAME__C || "N/A"}</li>
                    <li><strong>Influence:</strong> {contact.POSITION_OF_INFLUENCE__C || "Unknown"}</li>
                    <li>
                      <strong>LinkedIn Search:</strong>{" "}
                      <a href={contact.LINKEDIN_SEARCH__C} target="_blank" rel="noopener noreferrer">
                        {contact.LINKEDIN_SEARCH__C}
                      </a>
                    </li>
                    <li style={{ textAlign: "right" }}>
                      <strong>Last Modified:</strong> {new Date(contact.LAST_MODIFIED_DATE).toLocaleDateString()}
                    </li>
                  </ul>
                </div>
              ))}
            </div>
          </Panel>
        </Collapse>
      )}

      {/* Show spinner only when loading */}
      {loading && <Spinner />}
    </div>



  );
};

const News = ({ newsloading, newsData }) => {

  return (
    <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 16 }}>
      <Typography.Title
        level={5}
        style={{ borderBottom: "2px solid #52c41a", paddingBottom: 8 }}
      >
        Relevant News
      </Typography.Title>

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {newsloading ? (
          <Spinner />
        ) : newsData.length > 0 ? (
          <List
            dataSource={newsData}
            renderItem={(item, index) => (
              <List.Item
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: "12px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Typography.Text strong style={{ fontSize: 16 }}>
                  {item.Title}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ marginBottom: 8 }}>
                  {new Date(item.date).toLocaleDateString()}
                </Typography.Text>
                <Button
                  type="primary"
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                >
                  Read More
                </Button>
              </List.Item>
            )}
          />
        ) : (
          <Empty description="No news found" />
        )}
      </div>
    </div>
  )
}

export default SiteTabs;